<template>
    <div class="slider-wrap">
        <div ref="sliderRunway" class="slider-runway">
            <div
                class="slider-track"
                :style="{
                    width: sliderTrackWidth,
                    left: range ? sliderThumbMinLeft : 0
                }"
            ></div>
            <div
                ref="sliderThumbMin"
                @mousedown="e => onMouseDown(e, 'sliderThumbMin')"
                @touchstart="e => onTouchStart(e, 'sliderThumbMin')"
                class="slider-thumb slider-thumb-min"
                :style="{ left: sliderThumbMinLeft }"
            ></div>
            <div
                v-if="range"
                ref="sliderThumbMax"
                @mousedown="e => onMouseDown(e, 'sliderThumbMax')"
                @touchstart="e => onTouchStart(e, 'sliderThumbMax')"
                class="slider-thumb slider-thumb-max"
                :style="{ left: sliderThumbMaxLeft }"
            ></div>
            <ul v-if="showStops" class="slide-stops">
                <li
                    class="slide-stops-items"
                    v-for="(left, value) in stopOptions"
                    :key="value"
                    :style="{ left: left }"
                    @click="setValue(value)"
                ></li>
            </ul>
            <ul v-if="marks" class="slide-marks">
                <li
                    class="slide-marks-items"
                    v-for="(label, value) in marks"
                    :key="value"
                    :style="{ left: stopOptions[value] }"
                    @click="setValue(value)"
                >
                    {{ label }}
                </li>
            </ul>
        </div>
        <div v-if="showTooltip" class="slider-tip">
            <span>{{ min }}</span>
            <span>{{ max }}</span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Slider',
    emits: ['update:modelValue', 'change'],
    props: {
        modelValue: {
            type: [Number, Array],
            default: 0
        },
        min: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: 100
        },
        range: {
            type: Boolean,
            default: false
        },
        step: {
            type: Number,
            default: 1
        },
        showStops: {
            type: Boolean,
            default: false
        },
        marks: {
            type: Object,
            default: null
        },
        showTooltip: {
            type: Boolean,
            default: false
        },
        formatTooltip: {
            type: Function
        }
    },
    data: function() {
        return {
            isDragging: false,
            sliderRunway: null,
            sliderTrack: null,
            sliderThumb: null,
            sliderTrackWidth: 0,
            sliderThumbMinLeft: 0,
            sliderThumbMaxLeft: '100%',
            sliderThumbName: 'sliderThumbMin',
            stopOptions: {}
        };
    },
    watch: {
        modelValue(newValue) {
            this.initThumb();
        }
    },
    created() {
        this.initThumb();
        let { showStops, marks } = this;
        if (showStops || marks) {
            this.setStopOptions();
        }
    },
    methods: {
        initThumb() {
            let { max, min, modelValue } = this;
            let difference = max - min;
            if (this.range) {
                let [v1, v2] = modelValue;
                this.setThumb((v1 - min) / difference, 'sliderThumbMin', false);
                this.setThumb((v2 - min) / difference, 'sliderThumbMax', false);
            } else {
                this.setThumb(
                    (modelValue - min) / difference,
                    'sliderThumbMin',
                    false
                );
            }
        },
        setValue(val) {
            let { max, min, modelValue } = this;
            let difference = max - min;
            if (Array.isArray(modelValue)) {
                let [minVal, maxVal] = modelValue;
                let changeMax = maxVal - val < val - minVal;
                let name = changeMax ? 'sliderThumbMax' : 'sliderThumbMin';
                this.setThumb((val - min) / difference, name);
            } else {
                this.setThumb((val - min) / difference, 'sliderThumbMin');
            }
        },
        setStopOptions() {
            let { max, min, step } = this;
            let difference = max - min;

            let temp = min;
            while (temp < max) {
                let rate = ((temp - min) / difference) * 100;
                this.stopOptions[temp] = `${rate}%`;
                temp += step;
            }
            this.stopOptions[max] = `100%`;
        },
        emitValue(val) {
            this.$emit('update:modelValue', val);
            this.$emit('change', val);
        },
        setThumb(rate, sliderThumbName, flag = true) {
            let { min, max, modelValue, range, step } = this;
            let difference = max - min;

            // 修正value
            let val = rate * difference + min;
            val = min + Math.ceil((val - min) / step) * step;
            val = Math.min(max, val);
            val = Math.max(min, val);
            // 修正rate => 百分比
            rate = ((val - min) / difference) * 100;

            // set modelValue
            let outputValue = val;
            if (range) {
                let [v1, v2] = modelValue;
                if (sliderThumbName === 'sliderThumbMin') {
                    v1 = val;
                } else {
                    v2 = val;
                }
                outputValue = [v1, v2];
                this.sliderTrackWidth = `${((v2 - v1) / difference) * 100}%`;
            } else {
                this.sliderTrackWidth = `${rate}%`;
            }
            flag && this.emitValue(outputValue);
            this[sliderThumbName + 'Left'] = `${rate}%`;
        },
        startDragging(thumbRef) {
            this.isDragging = true;
            this.sliderThumbName = thumbRef;
            this.sliderThumb = this.$refs[thumbRef];
            this.sliderThumb.style.transition = 'none';
        },
        onMouseDown(e, thumbRef) {
            e.preventDefault();
            this.startDragging(thumbRef);
            document.addEventListener('mousemove', this.dragMove);
            document.addEventListener('mouseup', this.onMouseUp);
        },
        onMouseUp() {
            this.isDragging = false;
            this.sliderThumb.style.transition = 'all .3s';
            document.removeEventListener('mousemove', this.dragMove);
            document.removeEventListener('mouseup', this.onMouseUp);
        },
        onTouchStart(e, thumbRef) {
            e.preventDefault();
            this.startDragging(thumbRef);
            document.addEventListener('touchmove', this.dragMove, {
                passive: false
            });
            document.addEventListener('touchend', this.onTouchEnd);
        },
        onTouchEnd(e) {
            e.preventDefault();
            this.isDragging = false;
            this.sliderThumb.style.transition = 'all .3s';
            document.removeEventListener('touchmove', this.dragMove);
            document.removeEventListener('touchend', this.onTouchEnd);
        },
        dragMove(event) {
            let { sliderRunway } = this.$refs;
            let sliderThumb = this.sliderThumb;
            if (!this.isDragging) {
                return; 
            }
            const thumbWidth = sliderThumb.offsetWidth;
            const containerRect = sliderRunway.getBoundingClientRect();
            let { left, width } = containerRect;
            const clientX = event.clientX || event.touches[0].clientX;
            let offsetX = clientX - left - thumbWidth / 2;
            if (offsetX < 0) {
                offsetX = 0;
            } else if (offsetX > width) {
                offsetX = width;
            }
            this.setThumb(offsetX / width, this.sliderThumbName);
        }
    }
};
</script>
<style lang="scss">
.slider-wrap {
    width: 100%;
    height: 30px;
    .slider-runway {
        background: #ebecf1;
        height: 6px;
        border-radius: 10px;
        position: relative;
        transform: translateY(14px);

        @media only screen and (max-width: 600px) {
            width: calc(100% - 40px);
            margin-left: 20px;
        }
    }
    .slider-track {
        background-color: var(--color-theme);
        border-radius: 10px 0 0 10px;
        height: 6px;
        position: absolute;
        left: 0;
    }
    .slider-thumb {
        box-sizing: border-box;
        border-radius: 50%;
        width: 20px;
        height: 20px;
        background: #ffffff;
        border: 2px solid var(--color-theme);
        box-shadow: 0px 0px 10px rgba(0, 10, 30, 0.1);
        cursor: pointer;
        position: absolute;
        z-index: 1;
        top: 50%;
        left: 0;
        transform: translate(-50%, -50%);
        &::after {
            content: '';
            width: 30px;
            height: 30px;
            box-sizing: border-box;
            position: absolute;
            left: -5px;
            top: -5px;
        }
        @media only screen and (max-width: 600px) {
            width: 24px;
            height: 24px;
        }

        &:hover {
            box-shadow: 0px 0px 10px rgba(0, 10, 30, 0.15);
        }
    }
    .slider-tip {
        display: flex;
        justify-content: space-between;
        font-weight: 500;
        font-size: 14px;
        line-height: 17px;
        color: #a0a3af;

        @media only screen and (max-width: 600px) {
            font-weight: 400;
            padding: 0 5px;
        }
    }
    .slider-tooltip {
        position: absolute;
        top: -54px;
        padding: 8px 16px;
        font-size: 18px;
        line-height: 20px;
        color: #ffffff;
        background: var(--color-theme);
        border-radius: 6px;
        transform: translateX(-50%);
        touch-action: none; /* 禁止移动端滚动 */
        &::after {
            content: '';
            display: inline-block;
            width: 10px;
            height: 10px;
            transform-origin: center center;
            transform: rotate(45deg);
            position: absolute;
            bottom: -3px;
            left: calc(50% - 4px);
            background: var(--color-theme);
        }
        @media only screen and (max-width: 600px) {
            padding: 10px 16px;
        }
    }
    .slide-stops {
        display: flex;
        width: 100%;
        height: 0;
    }
    .slide-stops-items {
        position: absolute;
        width: 6px;
        height: 6px;
        background-color: #fff;
        border-radius: 50%;
        transform: translateX(-50%);
        cursor: pointer;
    }

    .slide-marks {
        display: flex;
        width: 100%;
        height: 0;
        list-style: none;
    }
    .slide-marks-items {
        position: absolute;
        transform: translateX(-50%);
        color: #4a4a60;
        font-size: 14px;
        margin-top: 15px;
        cursor: pointer;
    }
}
</style>
